import { fetchAPI, API_BASE_URL } from "./apiCore";
import { getToken, getuserId } from "./auth";

const normalizeUserManagementPage = (payload, fallbackPage = 0, fallbackSize = 25) => {
  if (Array.isArray(payload?.content)) {
    return payload;
  }

  if (Array.isArray(payload?.users)) {
    const pageNumber = typeof payload.page === "number" ? payload.page : fallbackPage;
    const pageSize = typeof payload.limit === "number" ? payload.limit : fallbackSize;
    const totalElements =
      typeof payload.total === "number" ? payload.total : payload.users.length;

    return {
      content: payload.users,
      totalElements,
      pageNumber,
      pageSize,
      totalPages:
        typeof payload.totalPages === "number"
          ? payload.totalPages
          : pageSize > 0
            ? Math.ceil(totalElements / pageSize)
            : 0,
    };
  }

  return {
    content: [],
    totalElements: 0,
    pageNumber: fallbackPage,
    pageSize: fallbackSize,
    totalPages: 0,
  };
};


// Fetch Department Options
export const fetchDepartmentOptions = async () => {
  try {
    return await fetchAPI("general/departments", "GET");
  } catch (error) {
    console.error("Error fetching department options:", error);
    return [];
  }
};


// Fetch Job Role Options
export const fetchJobRoleOptions = async () => {
  try {
    return await fetchAPI("general/job-roles", "GET");
  } catch (error) {
    console.error("Error fetching job role options:", error);
    return [];
  }
};


// Fetch Purpose Options
export const fetchPurposeOptions = async () => {
  try {
    return await fetchAPI("general/purposes", "GET");
  } catch (error) {
    console.error("Error fetching purpose options:", error);
    return [];
  }
};


// Save User Data
export const saveUserData = async (userData) => {
  return fetchAPI("users", "POST", {}, userData);
};


// Record a successful login.
// The server takes the timestamp from its own clock and throttles repeat calls,
// so this only needs to signal "the user is here", not when or how often.
export const pingLastLogin = async () => {
  const userId = getuserId();
  if (!userId) {
    return false;
  }

  await fetchAPI(`users/${userId}/last-login`, "POST");
  return true;
};



// Fetch User Data
export const fetchUserData = async () => {
  const userId = getuserId();
  if (!userId) {
    throw new Error("User ID not available");
  }

  try {
    return await fetchAPI(`users/${userId}`, "GET");
  } catch (error) {
    if (error.response?.status === 500) {
      console.warn("User data fetch failed with server error. Returning null.");
      return null;
    }
    throw error;
  }
};


// Update Department
export const updateUserDepartment = async (departments, otherDept) => {
  const userId = getuserId();
  if (!userId) throw new Error("User ID not available");

  const departmentValues = departments.map((dept) =>
    dept.value === "Other" && otherDept ? otherDept : dept.value
  );

  return fetchAPI(`users/${userId}/departments`, "PUT", {}, departmentValues);
};


// Update Job Role
export const updateJobRole = async (jobRoles, otherRole) => {
  const userId = getuserId();
  if (!userId) throw new Error("User ID not available");

  const jobRoleValues = jobRoles.map((job) =>
    job.value === "Other" && otherRole ? otherRole : job.value
  );

  return fetchAPI(`users/${userId}/job-roles`, "PUT", {}, jobRoleValues);
};


// Update Advisor
export const updateAdvisor = async (advisor) => {
  const userId = getuserId();
  if (!userId) {
    throw new Error("User ID not available");
  }
  if (advisor === "") {
    advisor = "Not Set";
  }
  return fetchAPI(`users/${userId}/advisor`, "PUT", {}, advisor);
};


// Update Purpose for Access
export const updatePurpose = async (purposes, otherPurpose) => {
  const userId = getuserId();
  if (!userId) throw new Error("User ID not available");

  const purposeValues = purposes.map((purpose) =>
    purpose.value === "Other" && otherPurpose ? otherPurpose : purpose.value
  );

  return fetchAPI(
    `users/${userId}/purpose-for-access`,
    "PUT",
    {},
    purposeValues
  );
};


// Fetch user management data with filtering and pagination
export const fetchUserManagementData = async (params = {}) => {
  const resolvedSize = params.size ?? params.limit ?? 25;
  const resolvedPage = params.page ?? 0;

  try {
    const {
      page = 0,
      size,
      limit,
      search = "",
      status = [], // Array of statuses to filter by ("Active" / "Inactive")
      roles = [], // Array of roles to filter by
      sortBy = "lastName",
      sortOrder = "asc",
    } = params;
    const requestedSize = size ?? limit ?? resolvedSize;

    const queryParams = {
      page,
      size: requestedSize,
      sortBy,
      sortOrder,
    };

    // Add search parameter if provided
    if (search.trim()) {
      queryParams.search = search.trim();
    }

    // Add status filter if provided
    if (status.length > 0) {
      queryParams.status = status;
    }

    // Add roles filter if provided
    if (roles.length > 0) {
      queryParams.roles = roles;
    }

    const response = await fetchAPI("admin/users", "GET", queryParams);
    return normalizeUserManagementPage(response, page, requestedSize);
  } catch (error) {
    console.error("Error fetching user management data:", error);
    return normalizeUserManagementPage(null, resolvedPage, resolvedSize);
  }
};


// Update user information
export const updateUserInformation = async (
  userId,
  userData,
  reason,
  comments
) => {
  try {
    const payload = {
      ...userData,
      reason,
      comments,
    };
    return await fetchAPI(`admin/users/${userId}`, "PUT", {}, payload);
  } catch (error) {
    console.error("Error updating user information:", error);
    throw error;
  }
};


// Get user details for editing
export const getUserDetails = async (userId) => {
  try {
    return await fetchAPI(`admin/users/${userId}`, "GET");
  } catch (error) {
    console.error("Error fetching user details:", error);
    throw error;
  }
};


// User Permissions API
export const getUserPermissionsByUserId = async (userId) => {
  try {
    return await fetchAPI(`biobank/user-permissions/user/${userId}`, "POST");
  } catch (error) {
    console.error("Error fetching user permissions:", error);
    throw error;
  }
};

export const getUserPermissionById = async (permissionId) => {
  try {
    return await fetchAPI(`biobank/user-permissions/${permissionId}`, "GET");
  } catch (error) {
    console.error("Error fetching user permission by ID:", error);
    throw error;
  }
};


export const saveUserPermission = async (userPermission) => {
  try {
    return await fetchAPI(
      "biobank/user-permissions",
      "POST",
      {},
      userPermission
    );
  } catch (error) {
    console.error("Error saving user permission:", error);
    throw error;
  }
};


export const patchUserPermission = async (permissionId, patchData) => {
  try {
    return await fetchAPI(
      `biobank/user-permissions/${permissionId}`,
      "PATCH",
      {},
      patchData
    );
  } catch (error) {
    console.error("Error patching user permission:", error);
    throw error;
  }
};


export const deleteUserPermission = async (permissionId) => {
  try {
    return await fetchAPI(`biobank/user-permissions/${permissionId}`, "DELETE");
  } catch (error) {
    console.error("Error deleting user permission:", error);
    throw error;
  }
};


export const updateUserPermissions = async (userPermissionUpdateRequest) => {
  try {
    return await fetchAPI(
      "biobank/user-permissions/update",
      "POST",
      {},
      userPermissionUpdateRequest
    );
  } catch (error) {
    console.error("Error updating user permissions:", error);
    throw error;
  }
};


// Fetch user groups
export const fetchUserGroups = async () => {
  try {
    return await fetchAPI("users/groups", "GET");
  } catch (error) {
    console.error("Error fetching user groups:", error);
    return [];
  }
};
